import { Nullable } from '@e2m-customer-portal/shared';
import { useCallback, useEffect, useState } from 'react';

interface UseFocusResult {
  focused: boolean;
  focus: () => void;
  blur: () => void;
}

/**
 * Sets the focus onto a HTML element that was captured with the returned reference, or removes it again.
 *
 * @example
 * const ref = useRef();
 * const { focus } = useFocus(ref.current);
 * // ...
 * if (someCondition) {
 *   focus();
 * }
 * // ...
 * <TextInput ref={ref} />
 */
export function useFocus(element: Nullable<HTMLElement>): UseFocusResult {
  const [focused, setFocused] = useState(element === document.activeElement);

  const focus = useCallback(() => element?.focus(), [element]);
  const blur = useCallback(() => element?.blur(), [element]);

  const gainFocus = () => setFocused(true);
  const loseFocus = () => setFocused(false);

  useEffect(() => {
    if (!element) return undefined;
    element.addEventListener('focus', gainFocus);
    return () => element.removeEventListener('focus', gainFocus);
  }, [element]);

  useEffect(() => {
    if (!element) return undefined;
    element.addEventListener('blur', loseFocus);
    return () => element.removeEventListener('blur', loseFocus);
  }, [element]);

  return {
    focused,
    focus,
    blur,
  };
}
